/** Admin > Products > SERP Validation: filter options and labels (helpers/serp_validation.py). */

export const SV_BAND_OPTIONS = [
  { key: 'all', label: 'All' },
  { key: 'fail', label: 'Poisoned (below 0.5)' },
  { key: 'borderline', label: 'Borderline (0.5 to 0.65)' },
  { key: 'ok', label: 'Fine (0.65 and above)' },
  { key: 'empty', label: 'Empty page' },
]

export const SV_PROVIDER_OPTIONS = [
  { key: 'all', label: 'All' },
  { key: 'dataforseo', label: 'DataForSEO' },
  { key: 'serpapi', label: 'SerpApi' },
]

export const SV_SCENARIO_OPTIONS = [
  { key: 'all', label: 'All' },
  { key: 'A', label: 'A: primary fine' },
  { key: 'B', label: 'B: other provider used' },
  { key: 'C', label: 'C: AI decided' },
  { key: 'D', label: 'D: uncertain' },
]

export const SV_REVIEW_OPTIONS = [
  { key: 'all', label: 'All' },
  { key: 'queue', label: 'Review queue (no verdict yet)' },
  { key: 'flagged', label: 'A page below 0.5' },
  { key: 'short', label: 'Fewer than 10 competitors' },
  { key: 'uncertain', label: 'Uncertain runs' },
  { key: 'ai', label: 'AI asked' },
  { key: 'switched', label: 'Switched provider' },
  { key: 'sample', label: 'Passed sample' },
  { key: 'localized', label: 'Localization checked' },
]

export const SV_VERDICT_OPTIONS = [
  { key: 'all', label: 'All' },
  { key: 'none', label: 'No verdict' },
  { key: 'healthy', label: 'Healthy' },
  { key: 'poisoned', label: 'Poisoned' },
]

export const SV_PROVIDER_LABELS = { dataforseo: 'DataForSEO', serpapi: 'SerpApi' }

export const SV_DECISION_LABELS = {
  logged: 'Logged only',
  primary: 'Primary used',
  other: 'Other provider used',
  primary_ai: 'Primary used (AI confirmed)',
  other_ai: 'Other provider used (AI confirmed)',
  uncertain: 'Uncertain',
}

export const SV_PAGE2_LABELS = {
  kept: '',
  replaced: 'page 2 replaced',
  dropped_both: 'page 2 dropped (poisoned on both providers)',
  dropped_no_alt: 'page 2 dropped (poisoned, other provider unavailable)',
  both_failed: 'page 2 poisoned on both (kept)',
}

export const SV_BAND_TONES = { fail: 'dan', borderline: 'warn', ok: 'suc', empty: 'grey' }

/** Localized competitors (second search with the place appended): action labels of the Local column. */
export const SV_LOCALIZATION_LABELS = {
  skipped: '',
  not_triggered: 'local: fine',
  ai_declined: 'local: AI said fine',
  localized: 'localized search used',
  local_failed: 'localized search failed',
}

export const SV_LOCALIZATION_REASONS = {
  mode: '',
  country_level: 'country-level location',
  keyword_has_place: 'keyword names a place',
  no_rows: 'no rows',
  fetch: 'second search failed',
  poisoned: 'second search poisoned',
  still_outside: 'second search still off-location',
}

export function svProvider (key) {
  return SV_PROVIDER_LABELS[key] || key || ''
}

export function svScore (value) {
  if (value === null || value === undefined || value === '') return ''
  return Number(value).toFixed(2)
}

export function svMoney (value) {
  const n = Number(value)
  if (!Number.isFinite(n) || n === 0) return ''
  return '$' + n.toFixed(4)
}

/**
 * Tooltip texts of the admin tab: one array entry per paragraph, a string or
 * { chip: { kind, value, label }, text } to start the paragraph with a coloured label (SvTip.vue).
 */
const score = (value, label) => ({ kind: 'score', value, label })
const scenario = (value) => ({ kind: 'scenario', value, label: 'Scenario ' + value })
const verdict = (value, label) => ({ kind: 'verdict', value, label })

export const SV_SCORE_LEGEND = [
  { chip: score('fail', 'below 0.50'), text: 'poisoned: Google answered one word of the keyword only (pages about "Austin" instead of "plumber austin tx").' },
  { chip: score('borderline', '0.50 to 0.64'), text: 'borderline: needs a human look.' },
  { chip: score('ok', '0.65 and above'), text: 'fine.' },
  { chip: score('empty', 'empty'), text: 'the page came back empty.' },
]

export const SV_SCENARIO_LEGEND = [
  { chip: scenario('A'), text: 'page 1 of the first provider was fine, POP used it. The normal case.' },
  { chip: scenario('B'), text: 'page 1 was not fine, POP fetched the other provider and used its page.' },
  { chip: scenario('C'), text: 'both providers looked doubtful, AI read the results and decided.' },
  { chip: scenario('D'), text: 'nothing could be verified: the run stopped and the user was told to retry, use the extension or add competitors by hand.' },
]

export const SV_VERDICT_LEGEND = [
  { chip: verdict('healthy', 'Healthy'), text: 'the results are about the keyword.' },
  { chip: verdict('poisoned', 'Poisoned'), text: 'the results answer one word of the keyword, or are otherwise off topic.' },
]

export const SV_SCENARIO_TIPS = {
  A: [SV_SCENARIO_LEGEND[0]],
  B: [SV_SCENARIO_LEGEND[1]],
  C: [SV_SCENARIO_LEGEND[2]],
  D: [SV_SCENARIO_LEGEND[3]],
}

export const SV_HEAD_TIPS = {
  date: ['When POP made this Google search (UTC). One row is one search made to find competitors for a report.'],
  keyword: ['The keyword searched, with the location, the language and the rule POP applied.',
    'single: a one-word keyword, the word must appear. local: the keyword contains the city of the location, a topic word and the city must appear. multi: at least two keyword words must appear.'],
  user: ['The account that started the run, and the report run when it is known.'],
  provider: ['The provider POP asked first: DataForSEO normally, SerpApi when DataForSEO failed.'],
  page1: ['Share of the 10 results on page 1 whose title, address or snippet mention the keyword. 1.00: all 10 do. 0.00: none.',
    ...SV_SCORE_LEGEND],
  page2: ['The same score on page 2 of the results. Page 2 is poisoned twice as often as page 1, usually under a fine page 1.',
    ...SV_SCORE_LEGEND],
  related: ['The same score on Google\'s "related searches".', 'A low value confirms a poisoned page. On its own it means nothing: healthy pages can score low here too.'],
  other: ['Filled only when POP had to fetch the second provider: its name, then its page 1 and page 2 scores, in the same colours.',
    ...SV_SCORE_LEGEND],
  decision: [...SV_SCENARIO_LEGEND,
    'The small text says which provider POP used in the end, whether page 2 was replaced by the other provider\'s page 2 or dropped because no clean page 2 existed, and whether AI was asked.'],
  comp: ['Usable competitor URLs left after blocked sites (YouTube, Reddit, Wikipedia...) and duplicates were removed.', 'POP picks 10. Fewer is not an error, but it is logged.'],
  verdict: ['Your reading of the results after opening Details.', ...SV_VERDICT_LEGEND,
    'The verdicts are what we use to adjust the thresholds (Tuning tab). "to review" marks rows still waiting for one.'],
  details: ['Opens both providers\' results side by side. Greyed rows are the ones that do not mention the keyword.'],
  local: ['For runs with a city or state location: share of the 10 page-1 results that provably target ANOTHER place (a city, region or country of POP\'s locations list that is not the run\'s). Results that name no place do not count.',
    'Above 50%, AI confirms it on the same results, then POP searches once more with the place appended to the keyword ("dog grooming portland") and takes the competitors from that search. Variations keep coming from the first search.',
    'Blank: country-level location, or the keyword already names a place.'],
}

export const SV_TILE_TIPS = {
  checked: ['Google searches scored in the selected period (dates, search text, provider filter). The other filters do not change the tiles.'],
  page1Fail: [{ chip: score('fail', 'below 0.50'), text: 'searches whose page 1 (first provider) scored below 0.50.' }],
  page2Fail: [{ chip: score('fail', 'below 0.50'), text: 'searches whose page 2 (first provider) scored below 0.50.' }],
  borderline: [{ chip: score('borderline', '0.50 to 0.64'), text: 'searches with a page in the borderline band (first provider).' }],
  switched: [{ chip: scenario('B'), text: 'searches where POP used the other provider\'s page 1 (also scenario C decided for the other provider).' }],
  page2Replaced: ['Searches where page 2 was taken from the other provider because the first one was poisoned.'],
  uncertain: [{ chip: scenario('D'), text: 'runs stopped because nothing could be verified.' }],
  aiCalls: [{ chip: scenario('C'), text: 'times the AI relevance check was asked.' }],
  extraCalls: ['Second-provider fetches made by the check, with their DataForSEO cost. SerpApi calls come out of the plan quota.'],
  shortLists: ['Runs that ended with fewer than 10 usable competitor URLs.'],
  reviewQueue: ['Rows in the Review Queue without a verdict yet: borderline pages, a sample of 1 passed page in 20, short lists and stopped runs.'],
  verdicts: ['Manual verdicts given in the period.', ...SV_VERDICT_LEGEND],
  localized: ['Searches whose competitors came from the second, localized search (place appended to the keyword).'],
  mode: ['log: POP only scores and records, nothing changes in the runs.', 'enforce: POP acts on what it finds: switches provider, replaces page 2, asks AI, stops unverifiable runs.'],
}

export const SV_TUNING_TIPS = {
  threshold: ['A possible threshold. The rows marked "in use" are the thresholds in the code today:',
    { chip: score('fail', 'below 0.50'), text: 'poisoned.' },
    { chip: score('borderline', '0.50 to 0.64'), text: 'borderline.' },
    { chip: score('ok', '0.65 and above'), text: 'fine.' }],
  page1Below: ['Stored page-1 results that would count as poisoned with this threshold (score below it).'],
  page2Below: ['Stored page-2 results that would count as poisoned with this threshold.'],
  poisonedBelow: [{ chip: verdict('poisoned', 'Poisoned'), text: 'pages you marked Poisoned that fall below this threshold: the ones the threshold would catch.' }],
  healthyBelow: [{ chip: verdict('healthy', 'Healthy'), text: 'pages you marked Healthy that fall below this threshold: false alarms the threshold would raise.' }],
  poisonedAtOrAbove: [{ chip: verdict('poisoned', 'Poisoned'), text: 'pages you marked Poisoned at or above this threshold: the ones it would miss.' }],
  healthyAtOrAbove: [{ chip: verdict('healthy', 'Healthy'), text: 'pages you marked Healthy at or above this threshold: correctly trusted.' }],
  score: ['Score range of the bucket. The bars show how many stored pages got a score in it.'],
  histPage1: ['Page-1 results in the bucket.'],
  histPage2: ['Page-2 results in the bucket.'],
}
